import React, { useEffect, useMemo, useState } from 'react';
import { gematriya } from '@hebcal/core';
import { BOAPI } from "../service/UsersService";

// אותו מיפוי בדיוק כמו MonthToNum בקליינט (react-app/functions/LetterToNum.js) —
// תשרי=1 ... אלול=12. קבוע, לא תאריך לועזי, כי שיעורים כמו תניא חוזרים על
// אותו יום/חודש עברי כל שנה.
const HEB_MONTHS = [
  { value: 1, label: 'תשרי' },
  { value: 2, label: 'חשון' },
  { value: 3, label: 'כסלו' },
  { value: 4, label: 'טבת' },
  { value: 5, label: 'שבט' },
  { value: 6, label: 'אדר' },
  { value: 7, label: 'ניסן' },
  { value: 8, label: 'אייר' },
  { value: 9, label: 'סיוון' },
  { value: 10, label: 'תמוז' },
  { value: 11, label: 'אב' },
  { value: 12, label: 'אלול' },
];

const monthLabel = (v) => HEB_MONTHS.find((m) => m.value === Number(v))?.label || v;

// ימי החודש העברי, 1-30, מוצגים בגימטריה (א' עד ל') — הערך הנשמר בפועל נשאר מספר רגיל.
const HEB_DAY_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1).map((d) => ({ value: d, label: gematriya(d) }));
const hebDayLabel = (d) => HEB_DAY_OPTIONS.find((o) => o.value === Number(d))?.label || d;

const defaultAssignForm = () => ({ decisionId: '', hebMonth: 1, hebDay: 1, hebDayTo: 1 });

// טופס שיוך מהיר — נפתח בתוך שורת השיעור, לא כטופס נפרד: בוחרים שיעור קבוע (רמב"ם/תניא/חומש
// וכו') + חודש עברי + יום (או טווח ימים — "עד יום" גדול מ"מיום" משייך את אותו שיעור לכל יום בטווח).
const AssignForm = ({ data, onChange, decisions, onSubmit, onCancel }) => (
  <div style={s.assignBox}>
    <div style={s.formRow}>
      <div style={s.formGroup}>
        <label style={s.label}>שיעור קבוע *</label>
        <select value={data.decisionId} onChange={(e) => onChange('decisionId', e.target.value)} style={s.input}>
          <option value="">בחר...</option>
          {decisions.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>חודש עברי *</label>
        <select value={data.hebMonth} onChange={(e) => onChange('hebMonth', Number(e.target.value))} style={s.input}>
          {HEB_MONTHS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>מיום *</label>
        <select value={data.hebDay} onChange={(e) => onChange('hebDay', Number(e.target.value))} style={s.input}>
          {HEB_DAY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div style={s.formGroup}>
        <label style={s.label}>עד יום (אופציונלי — לטווח ימים)</label>
        <select value={data.hebDayTo} onChange={(e) => onChange('hebDayTo', Number(e.target.value))} style={s.input}>
          {HEB_DAY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
    {data.hebDayTo > data.hebDay && (
      <div style={s.rangeHint}>
        ישוייך לכל יום מ-{hebDayLabel(data.hebDay)} עד {hebDayLabel(data.hebDayTo)} ({data.hebDayTo - data.hebDay + 1} ימים) ב{monthLabel(data.hebMonth)}.
      </div>
    )}
    <div style={s.formButtons}>
      <button onClick={onSubmit} style={s.saveButton}>➕ שייך</button>
      <button onClick={onCancel} style={s.cancelButton}>✕ בטל</button>
    </div>
  </div>
);

const RelatedLessonsTab = () => {
  const [files, setFiles] = useState([]);
  const [assignmentsByFile, setAssignmentsByFile] = useState(new Map());
  const [decisions, setDecisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');

  // ── שורת שיעור-שמע שבה כרגע פתוח טופס "שייך תאריך חדש". ──
  const [assigningFileId, setAssigningFileId] = useState(null);
  const [assignData, setAssignData] = useState(defaultAssignForm());

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [filesResp, assignmentsResp, decisionsResp, categoriesResp] = await Promise.all([
        BOAPI.searchMp3Files(),
        BOAPI.fetchRelatedLessons(),
        BOAPI.fetchDecisions(),
        BOAPI.fetchMp3Categories(),
      ]);
      applyFilesResp(filesResp);
      applyAssignmentsResp(assignmentsResp);
      if (Array.isArray(decisionsResp)) {
        const [statusObj, data] = decisionsResp;
        if (statusObj[0]?.code === 0 && Array.isArray(data)) setDecisions(data);
      }
      if (Array.isArray(categoriesResp)) {
        const [statusObj, data] = categoriesResp;
        if (statusObj[0]?.code === 0 && Array.isArray(data)) setCategories(data);
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const applyFilesResp = (resp) => {
    if (Array.isArray(resp)) {
      const [statusObj, data] = resp;
      if (statusObj[0]?.code === 0 && Array.isArray(data)) setFiles(data);
      else setError(statusObj[0]?.msg || 'שגיאה בטעינת שיעורי השמע');
    }
  };

  const applyAssignmentsResp = (resp) => {
    if (Array.isArray(resp)) {
      const [statusObj, data] = resp;
      if (statusObj[0]?.code === 0 && Array.isArray(data)) {
        const map = new Map();
        for (const row of data) {
          if (!map.has(row.mp3Id)) map.set(row.mp3Id, []);
          map.get(row.mp3Id).push(row);
        }
        setAssignmentsByFile(map);
      }
    }
  };

  const reloadAssignments = async () => {
    applyAssignmentsResp(await BOAPI.fetchRelatedLessons());
  };

  // חיפוש+סינון חי של רשימת השיעורים הראשית — עם דיבאונס קל.
  useEffect(() => {
    const t = setTimeout(async () => {
      applyFilesResp(await BOAPI.searchMp3Files(query.trim() || undefined, categoryId || undefined));
    }, 300);
    return () => clearTimeout(t);
  }, [query, categoryId]);

  const openAssign = (fileId) => {
    setAssigningFileId(fileId);
    setAssignData(defaultAssignForm());
  };

  const handleAssignSubmit = async (fileId) => {
    if (!assignData.decisionId) { setError('נא לבחור שיעור קבוע'); return; }
    const fromDay = Number(assignData.hebDay);
    const toDay = Number(assignData.hebDayTo) || fromDay;
    if (!fromDay || fromDay < 1 || fromDay > 30 || toDay < 1 || toDay > 30) { setError('יום בחודש לא תקין (1-30)'); return; }
    if (toDay < fromDay) { setError('"עד יום" לא יכול להיות לפני "מיום"'); return; }

    // טווח ימים — אותו שיוך (שיעור+חודש) נוצר בנפרד לכל יום בטווח (כולל שני הקצוות).
    const days = Array.from({ length: toDay - fromDay + 1 }, (_, i) => fromDay + i);

    try {
      const results = await Promise.all(days.map((hebDay) =>
        BOAPI.addRelatedLesson({
          decisionId: Number(assignData.decisionId),
          hebMonth: assignData.hebMonth,
          hebDay,
          mp3Id: fileId,
          sortOrder: 0,
        })
      ));
      const failed = results
        .map((resp, i) => ({ resp, day: days[i] }))
        .filter(({ resp }) => !(Array.isArray(resp) && resp[0]?.[0]?.code === 0));

      await reloadAssignments();
      if (failed.length === 0) {
        setAssigningFileId(null);
        setError(null);
      } else {
        const firstMsg = failed[0].resp?.[0]?.[0]?.msg;
        setError(
          failed.length === days.length
            ? (firstMsg || 'שגיאה בשיוך')
            : `שויכו ${days.length - failed.length} מתוך ${days.length} ימים — נכשל: ${failed.map((f) => hebDayLabel(f.day)).join(', ')}${firstMsg ? ` (${firstMsg})` : ''}`
        );
      }
    } catch (err) {
      setError(err.message || 'שגיאה בשיוך');
    }
  };

  const handleToggleAssignmentActive = async (row) => {
    try {
      const resp = await BOAPI.updateRelatedLesson({
        id: row.id,
        decisionId: row.decisionId,
        hebMonth: row.hebMonth,
        hebDay: row.hebDay,
        mp3Id: row.mp3Id,
        sortOrder: row.sortOrder,
        isActive: !row.isActive,
      });
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) await reloadAssignments();
        else setError(statusObj[0]?.msg || 'שגיאה בעדכון');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בעדכון');
    }
  };

  const handleRemoveAssignment = async (row) => {
    if (!window.confirm(`להסיר את השיוך ל"${row.decisionName || row.decisionId}, ${hebDayLabel(row.hebDay)} ${monthLabel(row.hebMonth)}"?`)) return;
    try {
      const resp = await BOAPI.deleteRelatedLesson(row.id);
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) await reloadAssignments();
        else setError(statusObj[0]?.msg || 'שגיאה במחיקה');
      }
    } catch (err) {
      setError(err.message || 'שגיאה במחיקה');
    }
  };

  const sortedFiles = useMemo(() =>
    [...files].sort((a, b) => (a.categoryName || '').localeCompare(b.categoryName || '') || a.title.localeCompare(b.title)),
    [files]
  );

  if (loading) return <div style={s.center}>טוען...</div>;

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <h2 style={s.title}>שיעורים בנושא</h2>
      </div>
      <p style={s.hint}>
        כל שיעורי השמע הקיימים בספרייה. לכל שיעור אפשר לשייך יום/חודש עברי קבוע (למשל תניא של ג' אב) —
        חוזר כל שנה, לא תלוי בתאריך לועזי. שיעור אחד יכול להיות משויך למספר תאריכים.
      </p>

      {error && (
        <div style={s.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={s.closeErrorButton}>✕</button>
        </div>
      )}

      <div style={s.pickerRow}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ ...s.input, flex: 2 }}
          placeholder="חפש שיעור לפי כותרת..."
        />
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} style={{ ...s.input, flex: 1 }}>
          <option value="">כל הרבנים</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {sortedFiles.length === 0 ? (
        <div style={s.noData}>אין שיעורים להצגה</div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow}>
                <th style={s.th}>רב</th>
                <th style={s.th}>שיעור</th>
                <th style={s.th}>שיוכים</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map((file) => {
                const assignments = assignmentsByFile.get(file.id) || [];
                const isAssigning = assigningFileId === file.id;
                return (
                  <React.Fragment key={file.id}>
                    <tr style={s.row}>
                      <td style={s.td}>{file.categoryName || 'ללא רב'}</td>
                      <td style={s.td}>{file.title}</td>
                      <td style={s.td}>
                        {assignments.length === 0 ? (
                          <span style={s.noAssignments}>לא משויך</span>
                        ) : (
                          <div style={s.chipsRow}>
                            {assignments.map((row) => (
                              <div key={row.id} style={{ ...s.chip, ...(row.isActive ? {} : s.chipInactive) }}>
                                <span onClick={() => handleToggleAssignmentActive(row)} style={s.chipLabel}>
                                  {row.decisionName || row.decisionId} — {hebDayLabel(row.hebDay)} {monthLabel(row.hebMonth)}
                                  {!row.isActive && ' (לא פעיל)'}
                                </span>
                                <button onClick={() => handleRemoveAssignment(row)} style={s.chipRemoveBtn}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ ...s.td, textAlign: 'left' }}>
                        {!isAssigning && (
                          <button onClick={() => openAssign(file.id)} style={s.addSmallButton}>➕ שייך תאריך</button>
                        )}
                      </td>
                    </tr>
                    {isAssigning && (
                      <tr>
                        <td style={s.td} colSpan={4}>
                          <AssignForm
                            data={assignData}
                            onChange={(field, value) => setAssignData((prev) => ({ ...prev, [field]: value }))}
                            decisions={decisions}
                            onSubmit={() => handleAssignSubmit(file.id)}
                            onCancel={() => setAssigningFileId(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },
  hint: { color: '#697386', fontSize: '13px', marginBottom: '20px', lineHeight: 1.6 },
  addSmallButton: { padding: '8px 14px', backgroundColor: '#eef7ee', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' },

  formGroup: { marginBottom: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'rtl', textAlign: 'right', boxSizing: 'border-box' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },

  assignBox: { backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee', padding: '14px' },
  rangeHint: { fontSize: '12px', color: '#8a6d1f', backgroundColor: '#fff8e1', border: '1px solid #f0dca0', borderRadius: '4px', padding: '8px 10px', marginBottom: '12px' },

  chipsRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  chip: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', backgroundColor: '#eef7ee', border: '1px solid #c8e6c9', borderRadius: '16px', fontSize: '13px' },
  chipInactive: { backgroundColor: '#f5f5f5', border: '1px solid #ddd', color: '#999' },
  chipLabel: { cursor: 'pointer' },
  chipRemoveBtn: { background: 'transparent', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '12px', padding: 0 },
  noAssignments: { color: '#bbb', fontSize: '13px' },
  pickerRow: { display: 'flex', gap: '8px', marginBottom: '16px' },

  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow: { backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' },
  th: { padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '2px solid #ddd' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '15px', color: '#666', fontSize: '14px', textAlign: 'right', verticalAlign: 'middle' },

  saveButton: { padding: '8px 14px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  cancelButton: { padding: '8px 14px', backgroundColor: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default RelatedLessonsTab;
